import { useState, useEffect } from 'react';
import NicknameBar from '../components/NicknameBar';
import TeamBadge from '../components/TeamBadge';
import OrnamentSeparator from '../components/OrnamentSeparator';

interface SheetVoteResultsScreenProps {
  voteId: string;
}

interface VoteResults {
  voteId: string;
  teamColor: string;
  totalVotes: number;
  results: Array<{
    option: { type: 'sheet' | 'random'; sheetId?: string };
    count: number;
    percentage: number;
  }>;
  isActive: boolean;
}

interface SheetDetails {
  id: string;
  name: string;
}

/**
 * Sheet Vote Results Screen (Audience)
 *
 * Mostra i risultati della votazione per la squadra dell'utente.
 */
function SheetVoteResultsScreen({ voteId }: SheetVoteResultsScreenProps) {
  const [voteData, setVoteData] = useState<VoteResults | null>(null);
  const [sheets, setSheets] = useState<Map<string, SheetDetails>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVoteResults();
  }, [voteId]);

  const loadVoteResults = async () => {
    try {
      const response = await fetch(`/api/sheet-votes/${voteId}/results`);
      if (!response.ok) throw new Error('Failed to load results');

      const data = await response.json();
      setVoteData(data);

      // Fetch sheet names for all sheet options
      const sheetOptions = data.results
        .filter((r: any) => r.option.type === 'sheet' && r.option.sheetId)
        .map((r: any) => r.option.sheetId);

      for (const sheetId of sheetOptions) {
        if (!sheets.has(sheetId)) {
          const sheetResponse = await fetch(`/api/sheets/${sheetId}`);
          if (sheetResponse.ok) {
            const sheetData = await sheetResponse.json();
            setSheets((prev) => new Map(prev).set(sheetId, sheetData.sheet));
          }
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to load vote results:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400 mx-auto mb-4"></div>
          <p className="text-ink-light text-xl font-heading">Caricamento risultati...</p>
        </div>
      </div>
    );
  }

  if (!voteData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-team-red text-xl">Risultati non disponibili</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 pt-10">
      <NicknameBar />
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-heading font-bold text-ink mb-3">
            Risultati Votazione
          </h1>
          <TeamBadge teamColor={voteData.teamColor} size="lg" />
        </div>

        <OrnamentSeparator className="mb-6" />

        {/* Results */}
        <div className="space-y-4">
          {voteData.results
            .sort((a, b) => b.percentage - a.percentage)
            .map((result, idx) => {
              const sheetName =
                result.option.type === 'random'
                  ? 'Casuale'
                  : sheets.get(result.option.sheetId!)?.name || 'Personaggio';

              const isWinner = idx === 0;

              return (
                <div
                  key={idx}
                  className={`parchment-card p-4 ${
                    isWinner ? 'ring-2 ring-team-yellow-accent' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-ink flex items-center gap-2">
                      {isWinner && <span>&#9758;</span>}
                      {result.option.type === 'random' && <span>&#127922;</span>}
                      {sheetName}
                    </span>
                    <span className="text-xl font-bold text-ink">
                      {result.percentage.toFixed(0)}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-4 bg-parchment-200/60 border border-parchment-300 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        isWinner
                          ? 'bg-team-yellow'
                          : result.option.type === 'random'
                          ? 'bg-parchment-400'
                          : 'bg-team-blue'
                      } transition-all duration-500 ease-out`}
                      style={{ width: `${result.percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-ink-light text-base">
            In attesa della proclamazione dell'eroe...
          </p>
        </div>
      </div>
    </div>
  );
}

export default SheetVoteResultsScreen;
